"use client";

import { NFONE_SUPPORT_EMAIL } from "@newform/constants";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/design-system/button";
import { trpc } from "@/lib/trpc-client";
import { Caption } from "./applyFormPrimitives";
import { CreatorBonusCard } from "./CreatorBonusCard";

/**
 * The public landing page for the NewForm One creator program — the
 * destination for creator-acquisition ads.
 *
 * It reads cold on a phone: someone arrives from an ad knowing nothing, and
 * has to understand what the program is, what they get, and what is being
 * asked of them before the CTA. The agreement query is fired here rather than
 * only on /join/apply so the form is warm by the time they tap through.
 */

const STEPS = ["apply", "brands", "shoot", "paid"] as const;

/**
 * These have to match the creator agreement, not sell past it. The agreement
 * is a work-made-for-hire: NewForm owns the footage a creator submits, and
 * that includes submissions we never use. Saying "you own what you shoot"
 * here — as an earlier draft of this page did — would have had the landing
 * page contradicting the contract on the very next screen.
 */
const FACTS = ["independence", "likeness", "noMinimum"] as const;

export function JoinLanding() {
  const t = useTranslations("join.landing");
  // Prefetch: /join/apply needs this same payload, and warming it here means
  // the form renders without a spinner. Also tells us whether to show the CTA.
  const agreementQuery = trpc.creatorApplication.getAgreement.useQuery(
    undefined,
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const open = agreementQuery.data?.open ?? null;

  return (
    <div className="min-h-screen bg-nf-ds-bg-surface">
      <div className="mx-auto w-full max-w-3xl px-nf-ds-6 py-nf-ds-16">
        <header className="flex flex-col gap-nf-ds-4">
          <Caption>{t("eyebrow")}</Caption>
          <h1 className="font-nf-ds-sans text-[40px] font-[600] leading-[1.05] tracking-[-0.02em] text-foreground sm:text-[52px]">
            {t("heading")}
          </h1>
          <p className="font-nf-ds-sans text-[17px] leading-[1.5] text-muted-foreground">
            {t("intro")}
          </p>

          {open === true ? <CreatorBonusCard /> : null}

          <div className="mt-nf-ds-2 flex flex-col items-start gap-nf-ds-3">
            {open === false ? (
              <div className="rounded-nf-ds-lg border border-border bg-card px-nf-ds-5 py-nf-ds-4 shadow-nf-ds-xs">
                <p className="font-nf-ds-sans text-[14px] text-foreground">
                  {t("closedTitle")}
                </p>
                <p className="mt-nf-ds-1 font-nf-ds-sans text-[13px] text-muted-foreground">
                  {t("closedBody")}
                </p>
              </div>
            ) : (
              <Button asChild variant="brand" size="lg">
                <Link href="/join/apply">{t("applyCta")}</Link>
              </Button>
            )}
            <p className="font-nf-ds-sans text-[13px] text-muted-foreground">
              {t("eligibility")}
            </p>
          </div>
        </header>

        <section className="mt-nf-ds-16">
          <Caption>{t("howItWorks")}</Caption>
          <ol className="mt-nf-ds-4 divide-y divide-border border-y border-border">
            {STEPS.map((step, index) => (
              <li
                key={step}
                className="flex gap-nf-ds-5 py-nf-ds-5 first:pt-nf-ds-4"
              >
                <span className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground leading-none pt-nf-ds-1">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="flex min-w-0 flex-col gap-nf-ds-1">
                  <h2 className="font-nf-ds-sans text-[16px] font-[600] leading-[1.25] tracking-[-0.005em]">
                    {t(`steps.${step}.title`)}
                  </h2>
                  <p className="font-nf-ds-sans text-[14px] leading-[1.5] text-muted-foreground">
                    {t(`steps.${step}.body`)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-nf-ds-12 grid gap-nf-ds-4 sm:grid-cols-3">
          {FACTS.map((fact) => (
            <div
              key={fact}
              className="rounded-nf-ds-lg border border-border bg-card p-nf-ds-6 shadow-nf-ds-xs"
            >
              <h3 className="font-nf-ds-sans text-[15px] font-[600] leading-[1.25] tracking-[-0.005em]">
                {t(`facts.${fact}.title`)}
              </h3>
              <p className="mt-nf-ds-2 font-nf-ds-sans text-[13px] leading-[1.5] text-muted-foreground">
                {t(`facts.${fact}.body`)}
              </p>
            </div>
          ))}
        </section>

        <section className="mt-nf-ds-12 rounded-nf-ds-lg border border-border bg-card p-nf-ds-6 shadow-nf-ds-xs">
          <Caption>{t("lookFor.title")}</Caption>
          <p className="mt-nf-ds-3 font-nf-ds-sans text-[14px] leading-[1.6] text-muted-foreground">
            {t("lookFor.body")}
          </p>
        </section>

        {open !== false ? (
          <div className="mt-nf-ds-12 flex flex-col items-start gap-nf-ds-3 border-t border-border pt-nf-ds-8">
            <h2 className="font-nf-ds-sans text-[22px] font-[600] leading-[1.15] tracking-[-0.01em]">
              {t("ready")}
            </h2>
            <Button asChild variant="brand" size="lg">
              <Link href="/join/apply">{t("applyCta")}</Link>
            </Button>
          </div>
        ) : null}

        <p className="mt-nf-ds-12 font-nf-ds-sans text-[13px] text-muted-foreground">
          {t.rich("questions", {
            email: NFONE_SUPPORT_EMAIL,
            link: (chunks) => (
              <a
                href={`mailto:${NFONE_SUPPORT_EMAIL}`}
                className="underline underline-offset-2"
              >
                {chunks}
              </a>
            ),
          })}
        </p>
      </div>
    </div>
  );
}
